import { useState } from 'react';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import type { App, Project } from '@hangar/shared';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AppHeader } from '@/features/app-page/AppHeader';
import { AppOverview } from '@/features/app-page/AppOverview';
import { activeTab, APP_TABS, TAB_LABEL, TAB_SHORT_LABEL, tabSearch, type AppTab } from '@/features/app-page/appPageModel';
import { useAppPageData } from '@/features/app-page/useAppPageData';
import { ArchiveAppDialog } from '@/features/apps/ArchiveAppDialog';
import { EditAppSheet } from '@/features/apps/EditAppSheet';
import { RequirementsEditor } from '@/features/env/RequirementsEditor';
import { ProfilesPanel } from '@/features/profiles/ProfilesPanel';
import { StoresPanel } from '@/features/stores/StoresPanel';

export { APP_TABS, type AppTab };
export interface AppPageSearch {
  tab?: AppTab;
}

function AppNotFound({ project, slug, appSlug }: { project: Project | undefined; slug: string; appSlug: string }) {
  return (
    <EmptyState
      testId="app-not-found"
      icon="apps"
      title={project ? 'App not found' : 'Project not found'}
      description={project ? `${project.name} has no live app with slug “${appSlug}”.` : `There is no live project with slug “${slug}” in this organization.`}
      action={
        <Button asChild variant="outline">
          {project ? (
            <Link to="/projects/$slug" params={{ slug }}>
              Back to Apps &amp; profiles
            </Link>
          ) : (
            <Link to="/">Back to Overview</Link>
          )}
        </Button>
      }
    />
  );
}

function AppTabList() {
  return (
    <TabsList variant="line" aria-label="App sections">
      {APP_TABS.map((t) => (
        <TabsTrigger key={t} value={t} data-testid={`app-tab-${t}`} aria-label={TAB_SHORT_LABEL[t] ? TAB_LABEL[t] : undefined}>
          {TAB_SHORT_LABEL[t] ? (
            <>
              <span className="sm:hidden">{TAB_SHORT_LABEL[t]}</span>
              <span className="max-sm:hidden">{TAB_LABEL[t]}</span>
            </>
          ) : (
            TAB_LABEL[t]
          )}
        </TabsTrigger>
      ))}
    </TabsList>
  );
}

interface ReadyProps {
  slug: string;
  appSlug: string;
  project: Project;
  app: App;
  editing: boolean;
  onEditingChange: (open: boolean) => void;
}

function AppTabs({ app, project, onEdit, onTab, tab }: { app: App; project: Project; onEdit: () => void; onTab: (t: string) => void; tab: AppTab }) {
  return (
    <Tabs value={tab} onValueChange={onTab} className="gap-5">
      <AppTabList />
      <TabsContent value="overview">
        <AppOverview app={app} project={project} onEdit={onEdit} onTab={onTab} />
      </TabsContent>
      <TabsContent value="profiles">
        <ProfilesPanel app={app} project={project} />
      </TabsContent>
      <TabsContent value="env">
        <Card className="enter overflow-hidden">
          <RequirementsEditor key={app.id} apps={[app]} initialAppSlug={app.slug} scoped />
        </Card>
      </TabsContent>
      <TabsContent value="stores">
        <StoresPanel app={app} project={project} />
      </TabsContent>
      {/* The Shadow mode tab (ShadowCard) returns with docs/mvp-plan.md P18; archiving moved to the header's more menu. */}
    </Tabs>
  );
}

function AppPageReady({ slug, appSlug, project, app, editing, onEditingChange }: ReadyProps) {
  const search = useSearch({ strict: false }) as AppPageSearch;
  const navigate = useNavigate();
  const [archiving, setArchiving] = useState(false);
  const setTab = (t: string) => void navigate({ to: '/projects/$slug/apps/$app', params: { slug, app: appSlug }, search: tabSearch(t), replace: true, resetScroll: false });
  return (
    <div className="flex max-w-[1180px] flex-col gap-5" data-testid="app-page">
      <AppHeader app={app} project={project} onEdit={() => onEditingChange(true)} onArchive={() => setArchiving(true)} />
      <AppTabs app={app} project={project} onEdit={() => onEditingChange(true)} onTab={setTab} tab={activeTab(search.tab)} />
      <EditAppSheet app={editing ? app : null} onOpenChange={onEditingChange} />
      <ArchiveAppDialog app={archiving ? app : null} onOpenChange={setArchiving} onArchived={() => void navigate({ to: '/projects/$slug', params: { slug }, search: {} })} />
    </div>
  );
}

/**
 * Design 29 · The App page: one app of a project, with its own URL. Header (tile, name, framework · bundle id, provider,
 * Edit settings, more menu with Archive app), then tabs — Overview (settings, build profiles, recent builds), Profiles
 * (the full profiles panel with Run), Env requirements (the existing editor, scoped to the app) and Stores (the Google
 * Play apps and the tracks each environment may reach, P29). Edit settings is the
 * shared EditAppSheet: one decision of a few fields stays a sheet.
 */
export function AppPage() {
  const { slug, app: appSlug } = useParams({ strict: false }) as { slug: string; app: string };
  const data = useAppPageData(slug, appSlug);
  const [editing, setEditing] = useState(false);
  if (data.kind === 'loading') return <AppPageSkeleton />;
  if (data.kind === 'error') return <ErrorState error={data.error} onRetry={data.onRetry} />;
  if (data.kind === 'missing') return <AppNotFound project={data.project} slug={slug} appSlug={appSlug} />;
  return <AppPageReady slug={slug} appSlug={appSlug} project={data.project} app={data.app} editing={editing} onEditingChange={setEditing} />;
}

function AppPageSkeleton() {
  return (
    <div className="flex max-w-[1180px] flex-col gap-5" aria-busy="true" aria-label="Loading the app">
      <div className="flex items-start gap-4 border-b pb-5">
        <Skeleton className="size-14 rounded-xl" />
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-7 w-56" />
          <Skeleton className="h-4 w-72" />
        </div>
        <Skeleton className="h-9 w-32 max-sm:hidden" />
      </div>
      <div className="flex gap-4 border-b pb-2">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-5 w-20" />
        ))}
      </div>
      <Card>
        <CardHeader>
          <Skeleton className="h-4 w-48" />
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10" />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
